import React, { useCallback, useEffect, useState } from "react"
import Highcharts from "highcharts/highstock"
import HighchartsReact from "highcharts-react-official"
import styles from "./Chart.module.css"
import { convertTimestampToDate } from "@utils/time"
import moment from "moment"
import momentTimeZone from "moment-timezone"
import { formatCurrency } from "@utils/currency"

type LookupKey = keyof typeof lookup

// type ChartDataPoint = [number, number, number, number, number, number]
// type ChartData = Array<ChartDataPoint>
type ChartData = any // Couldn't find a way to make TypeScript happy at the time...

type Prices = {
  day?: ChartData
  week?: ChartData
  threeMonths?: ChartData
  ytd?: ChartData
  year?: ChartData
  fiveyears?: ChartData
}

type PriceKey = keyof Prices

type Data = {
  listingKey: string
  isin: string
  name: string
  companyName: string
  bcClose: string
  bcOpen: string
  prices: Prices
}

function createOptions(
  seriesName: string,
  chartData: ChartData,
  darkMode: boolean,
  activeTimeRange: string,
  listingKey: string,
) {
  return {
    title: {
      text: "",
    },
    legend: {
      enabled: false,
    },
    time: {
      timezone: "Europe/Zurich",
    },
    series: [
      {
        name: seriesName,
        marker: {
          enabled: false,
          fillColor: "#378EBD",
          lineWidth: 0,
          lineColor: "#378EBD",
        },
        data: chartData,
        threshold: null,
        yAxis: 0,
        type: "area",

        useOhlcData: listingKey !== "121222008-1329-1" && listingKey !== "274207-2913-333", // Do not use OHLC data for FuW 50 and Oil Brent because we are missing the opening prices, see hack in MarketDataController
        lineColor: "#378EBD",
        color: darkMode ? "#1D3745" : "#CDE2EE",
        fillOpacity: 0.8,
        lineWidth: 3,
        showInLegend: "false",
      },
    ],
    xAxis: {
      lineColor: darkMode ? "#e9e9e9" : "#7d7d7d",
      tickColor: darkMode ? "#e9e9e9" : "#7d7d7d",
      crosshair: false,
      type: "datetime",
      endOnTick: true,
      showLastLabel: false,
      ordinal: true,
      labels: {
        style: {
          color: darkMode ? "#e9e9e9" : "#7d7d7d",
        },
      },
    },
    yAxis: {
      enableMouseTracking: false,
      showLastLabel: false,
      startOnTick: false,
      labels: {
        style: {
          color: darkMode ? "#e9e9e9" : "#7d7d7d",
        },
        align: "left",
        x: 0,
      },
    },
    plotOptions: {
      series: {
        animation: {
          duration: 0,
        },
        states: {
          hover: {
            halo: {
              size: 0,
            },
          },
        },
      },
    },
    chart: {
      height: 220,
      marginTop: 0,
      marginLeft: 0,
      marginRight: 0,
      animation: false,
      backgroundColor: darkMode ? "#121212" : "#fff",
    },
    tooltip: {
      formatter: function (): any {
        return (
          "<div style='padding: 8px 10px; box-shadow: 0 0 4px 1px var(--shadowColor); color: var(--tooltipTextColor); background-color: var(--primary-color)'><div>" +
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          moment(this.x).format(
            ["day", "week", "month"].includes(activeTimeRange) ? "DD.MM.YYYY HH:mm" : "DD.MM.YYYY",
          ) +
          "</div><div><b>" +
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          formatCurrency(this.y) +
          "</b></div></div>"
        )
      },
      shadow: false,
      backgroundColor: "transparent",
      borderWidth: 0,
      borderRadius: 0,
      useHTML: true,
    },
    credits: {
      enabled: false,
    },
  }
}

const lookup = {
  day: {
    shortDesc: "INTRA",
    longDesc: "INTRA",
  },
  week: {
    shortDesc: "1W",
    longDesc: "1 Woche",
  },
  threeMonths: {
    shortDesc: "3M",
    longDesc: "3 Monate ",
  },
  ytd: {
    shortDesc: "YTD",
    longDesc: "YTD",
  },
  year: {
    shortDesc: "1Y",
    longDesc: "1 Jahr",
  },
  fiveyears: {
    shortDesc: "5Y",
    longDesc: "5 Jahre",
  },
}

const Error = () => (
  <div className={styles.error}>
    <span>Keine Daten vorhanden.</span>
  </div>
)

const Chart = ({
  wfgListingKey,
  active,
  defaultRange,
  darkMode,
}: {
  wfgListingKey: string
  active: boolean
  defaultRange?: PriceKey
  darkMode: boolean
}) => {
  const initialData = {
    listingKey: "",
    isin: "",
    name: "",
    companyName: "",
    bcClose: "",
    bcOpen: "",
    prices: {},
  }
  const [data, setData] = useState<Data>(initialData)
  const [isFetched, setIsFetched] = useState(false)
  const [timeRanges] = useState<Array<PriceKey>>(Object.keys(lookup) as Array<PriceKey>)
  const [activeTimeRange, setActiveTimeRange] = useState<PriceKey>(defaultRange ?? "year")
  const [displayError, setDisplayError] = useState<boolean>(false)

  const fetchData = useCallback(() => {
    fetch(`/api/boerse/marketdata/chartdata/${wfgListingKey}?timeRange=${timeRanges.join()}`)
      .then((res) => res.json())
      .then((json) => {
        if (json.status !== "Success") {
          setDisplayError(true)
          return Promise.reject(json.message)
        }
        // Alpine Select has a data point at 1744070400000 that is a bid, this leads to a skewed dataset.
        // According to AllFunds, the chartsdata endpoint can't be filtered by ask, bid and trade, so we have to make an exception for this case.
        // In the future, we should find a way to improve this. They recommend to use the hiku endpoint instead of the chartsdata endpoint.
        // Ticket: https://tamedia.atlassian.net/jira/software/c/projects/FUW/boards/87?selectedIssue=FUW-525&useStoredSettings=true
        if (json.payload.isin === "CH0019199550") {
          const ranges = ["day", "week", "threeMonths", "ytd", "year", "fiveyears"]
          const refinedPrices = ranges.reduce((acc: any, currentIndex) => {
            acc[currentIndex] = json.payload.prices[currentIndex].filter((item: any) => item[0] !== "1744070400000")
            return acc
          }, {})
          setData({ ...json.payload, prices: refinedPrices })
        } else {
          setData(json.payload)
        }
        setIsFetched(true)
      })
      .catch((error) => {
        console.error(error)
      })
  }, [wfgListingKey, timeRanges])

  useEffect(() => {
    // Language has to be set on global Higcharts options... https://api.highcharts.com/highcharts/lang
    Highcharts.setOptions({
      lang: {
        months: [
          "Januar",
          "Februar",
          "März",
          "April",
          "Mai",
          "Juni",
          "Juli",
          "August",
          "September",
          "Oktober",
          "November",
          "Dezember",
        ],
        shortMonths: ["Jan.", "Feb.", "Mrz.", "Apr.", "Mai", "Juni", "Juli", "Aug.", "Sep.", "Okt.", "Nov.", "Dez."],
        weekdays: ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"],
      },
    })
    window.moment = moment // Highcharts needs Moment.js in global space to work: https://api.highcharts.com/highcharts/time
    momentTimeZone()
  }, [])

  useEffect(() => {
    if (active && !isFetched) {
      fetchData()
    }
  }, [active, isFetched, fetchData])

  function handleClick(target: HTMLButtonElement) {
    setActiveTimeRange(target.dataset.range as PriceKey)
  }

  return (
    <div className={styles.boxChartContainer}>
      {displayError && <Error />}
      {active && isFetched && (
        <div>
          <nav>
            <ul className={styles.chartNav}>
              {Object.keys(data.prices).map((range, i) => (
                <li
                  className={`${styles.chartNavItem} ${activeTimeRange === range ? styles.active : ""} ${
                    data.prices[range as LookupKey].length === 0 ? styles.disabled : ""
                  }`}
                  key={i}
                  data-test-id={activeTimeRange === range ? "active" : "inactive"}
                >
                  <button
                    data-range={range}
                    className={styles.chartTab}
                    data-test-id={data.prices[range as LookupKey].length === 0 ? "active-button" : "inactive-button"}
                    disabled={data.prices[range as LookupKey].length === 0}
                    onClick={(e) => {
                      handleClick(e.currentTarget as HTMLButtonElement)
                    }}
                  >
                    <span className={styles.chartTabLongName}>{lookup[range as LookupKey].longDesc}</span>
                    <span className={styles.chartTabShortName}>{lookup[range as LookupKey].shortDesc}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
          {data.prices[activeTimeRange]?.length > 0 && (
            <div>
              <div className={styles.boxContentDate}>
                {convertTimestampToDate(data.prices[activeTimeRange]?.slice(-1)[0][0])}
              </div>
              <HighchartsReact
                highcharts={Highcharts}
                options={createOptions(
                  data.companyName,
                  [...data.prices[activeTimeRange]],
                  darkMode,
                  activeTimeRange,
                  data.listingKey,
                )} // Highcharts is changing the reference sometimes somehow, so I pass a clone. I couldn't figure out another way at the time...
              />
            </div>
          )}
          {data.prices[activeTimeRange]?.length === 0 && <Error />}
        </div>
      )}
    </div>
  )
}

export default Chart
